/**
 * Host-based tenant resolution — Phase 1 (subdomains).
 *
 * Club Central normally resolves the tenant from the first URL path
 * segment (see `getTenantId` / `getTenantSlugFromPath` in apiHelpers).
 * To let a club use a branded subdomain (`<slug>.clubcentral.app`) as an
 * ADDITIONAL front door, we also derive the tenant from the HOST.
 *
 * Phase 1 keeps it deliberately simple: the subdomain LABEL *is* the
 * tenant slug (`river-city-gym.clubcentral.app` → slug `river-city-gym`).
 * Short vanity labels and full custom domains, which need a
 * central-registry lookup, are Phase 2.
 *
 * Since @mediatechgroup/site-host the host-to-slug rule is the package's
 * `slugFromHost` over Club Central's one site-host config
 * (src/config/siteHost.ts: the platform hosts, the reserved labels, the
 * tenant base domain, and HOST_SLUG_CONFIG's exact match and two-character
 * minimum). This module reads the browser's hostname and hands it over.
 * src/api/__tests__/hostResolver.pinned.test.ts holds it to the
 * pre-package resolver's answers over the host matrix (PM b5bc6744).
 *
 * On every CURRENT live host (`clubs.clubcentral.app`, the Netlify staging
 * hosts, local) this returns null, so behavior is identical to today until
 * a wildcard `*.clubcentral.app` subdomain is actually provisioned.
 *
 * No dependency on apiHelpers (avoids a circular import — apiHelpers
 * imports from here).
 */
import { slugFromHost } from "@mediatechgroup/site-host";
import { HOST_SLUG_CONFIG } from "../config/siteHost";
import { resolvedCustomDomainSlugFor } from "./customDomainHost";

/**
 * The tenant slug a browser hostname implies, or null when the host is a
 * platform host, a reserved label, or not a Phase-1 tenant subdomain.
 */
export const tenantSlugForHostname = (
  hostname: string | null | undefined
): string | null => slugFromHost(hostname, HOST_SLUG_CONFIG);

/**
 * The tenant slug implied by the current HOST, or null when the host is
 * a platform host / not a Phase-1 tenant subdomain.
 */
export const getTenantSlugFromHost = (): string | null => {
  let hostname: string | undefined;
  try {
    hostname = window.location.hostname;
  } catch {
    return null;
  }
  // Phase 2 custom domains (PM 01fda321): on a club's own domain the slug
  // is the one the boot resolved for exactly this host (api/customDomainHost.ts).
  return tenantSlugForHostname(hostname) ?? resolvedCustomDomainSlugFor(hostname);
};

/** True when the current host is a tenant subdomain front door. */
export const isHostScopedTenant = (): boolean =>
  getTenantSlugFromHost() !== null;
